/**
 * @file sales-representative.tsx
 * @module app/modules/sales-management/sales-manager/sales-representative/sales-representative
 * @description Implements the sales representative interface for sales-management operations.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { useEffect, useState } from 'react';
import { Button, Card, Col, FormLabel, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getKey, setKey } from '../../../../../Global/history';
import Loader from '../../../../../Global/loader';
import Pagination from '../../../../../Global/pagination';
import { Manager, PAGE_LIMIT } from '../../../../../utils/constants';
import { listSalesRepresentative } from '../../../../../utils/storeString';
import { KTSVG } from '../../../../../_admin/helpers';
import { useDebounce } from '../../../../../utils/useDebounce';
import { CustomSelect } from '../../../../custom/Select/CustomSelect';
import { REPRESENTATIVE_STATUS, REPRESENTATIVE_TARGET_PERIOD } from '../../../../../utils/staticJSON';
import AddSalesRepresentativeModal from '../../../../modals/add-sales-representative-modal';
import APICallService from '../../../../../api/apiCallService';
import { assistAdmin } from '../../../../../api/apiEndPoints';
import Method from '../../../../../utils/methods';
import { success } from '../../../../../Global/toast';
import { masterToast } from '../../../../../utils/toast';
import ThreeDotMenu from '../../../../../_admin/assets/media/svg_uTrade/three-dot.svg';
import { CustomSelectTable } from '../../../../custom/Select/CustomSelectTable';
import { useAuth } from '../../../auth';
import { useNavigationHistory } from '../../../../../utils/useNavigationHistory';

const SalesRepresentative = () => {
  const navigate = useNavigate();
  const { navigateTo } = useNavigationHistory();
  const { currentUser } = useAuth();
  const [salesRepresentatives, setSalesRepresentatives] = useState<any>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [page, setPage] = useState<any>(getKey(listSalesRepresentative.page) || 1);
  const [pageLimit, setPageLimit] = useState<any>(
    getKey(listSalesRepresentative.limit) || PAGE_LIMIT
  );
  const [search, setSearch] = useState<any>(
    getKey(listSalesRepresentative.search) || ''
  );
  const [status, setStatus] = useState<any>(
    getKey(listSalesRepresentative.status)
      ? Number(getKey(listSalesRepresentative.status))
      : undefined
  );
  const [target, setTarget] = useState<any>(
    getKey(listSalesRepresentative.target) !== null
      ? Number(getKey(listSalesRepresentative.target))
      : -1
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [menuId, setMenuId] = useState<number>(-1);
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [selectedRep, setSelectedRep] = useState<any>(undefined);

  const menuOptions = [
    {
      label: (
        <button className="btn btn-link fs-14 fw-600 text-black ms-3 p-4">
          Edit
        </button>
      ),
      value: 1,
    }
  ];

  const onMenuOpen = (id: any) => {
    setMenuId(id);
    setShowMenu(true);
  };

  const onMenuClose = () => {
    setMenuId(-1);
    setShowMenu(false);
  };

  const openMenuOnClick = (id: any) => {
    setMenuId(id);
    setShowMenu(true);
  };

  const handleOption = (event: any, data: any) => {
    if (event.value === 1) {
      setSelectedRep(data);
      setShowAddModal(true);
    }
  };

  const handleToggleActivation = async (user: any, index: number) => {
    const apiService = new APICallService(
      assistAdmin.status,
      {
        active: `${user.status === 'Active' ? "false" : "true"}`,
      },
      { id: user._id },
      "",
      "",
      "",
      "",
      ""
    );
    const response = await apiService.callAPI();
    if (response) {
      if (user.status === 'Active') {
        success(masterToast.userDeactivated);
      } else {
        success(masterToast.userActivated);
      }
      const temp: any = [...salesRepresentatives];
      temp[index].status = user.status === 'Active' ? 'Inactive' : 'Active';
      setSalesRepresentatives(temp);
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchSalesRepresentatives(page, pageLimit, search, status, target);
    }
  }, [currentUser]);


  const fetchSalesRepresentatives = async (pageNo: number, limit: number, search: string, status: number, target: number) => {
    setLoading(true);
    let params: any = {
      pageNo: pageNo,
      limit: limit,
      sortKey: "createdAt",
      sortOrder: -1,
      needCount: true,
      "role[0]": currentUser?.role?.find((obj: any) => obj.type === Manager) ? 2 : 7,
      searchTerm: search,
      manager: currentUser?._id,
    };
    if (status !== undefined) {
      params.status = status;
    }
    if (target !== -1 && target !== undefined) {
      params.targetType = target;
    }
    let apiService = new APICallService(
      assistAdmin.list,
      params,
      "",
      "",
      "",
      "",
      "",
      ""
    );
    let response = await apiService.callAPI();
    // Fake data to match the provided design screenshot
    // let response = {
    //   total: 3,
    //   records: [
    //     {
    //       _id: 1,
    //       name: 'Joseph Kimaro',
    //       email: 'joseph.kimaro@utrade.co.tz',
    //       phoneCountry: '+255',
    //       phone: '754 123 456',
    //       assignedCustomers: 33,
    //       orders: 70,
    //       targetCurrent: 471163,
    //       targetGoal: 700000,
    //       targetPeriod: 'Monthly',
    //       status: 'Active',
    //     },
    //     {
    //       _id: 2,
    //       name: 'Hamisi Juma',
    //       email: 'hamisi.juma@utrade.co.tz',
    //       phoneCountry: '+255',
    //       phone: '712 345 678',
    //       assignedCustomers: 27,
    //       orders: 44,
    //       targetCurrent: 53163,
    //       targetGoal: 100000,
    //       targetPeriod: 'Monthly',
    //       status: 'Active',
    //     },
    //     {
    //       _id: 3,
    //       name: 'Baraka Mshana',
    //       email: 'baraka.mshana@utrade.co.tz',
    //       phoneCountry: '+255',
    //       phone: '765 432 109',
    //       assignedCustomers: 42,
    //       orders: 90,
    //       targetCurrent: 112500,
    //       targetGoal: 200000,
    //       targetPeriod: 'Yearly',
    //       status: 'Inactive',
    //     },
    //   ],
    // };
    if (response.records) {
      setTotalRecords(response.total);
      const mappedRecords = response.records.map((val: any) => {
        return {
          ...val,
          status: val.active ? "Active" : "Inactive"
        };
      });
      setSalesRepresentatives(mappedRecords);
    }
    setLoading(false);
  };
  const debounce = useDebounce(fetchSalesRepresentatives, 300);
  const handleSearch = async (value: string) => {
    setLoading(true);
    setSearch(value);
    setPage(1);
    setKey(listSalesRepresentative.page, 1);
    setKey(listSalesRepresentative.search, value, false);

    await debounce(1, pageLimit, value, status, target);
  };
  const handleStatusFilter = async (value: number) => {
    setStatus(value);
    setKey(listSalesRepresentative.status, value);
    setKey(listSalesRepresentative.page, 1);
    setLoading(true);
    setPage(1);
    await fetchSalesRepresentatives(
      1,
      pageLimit,
      search,
      value,
      target
    );
    setLoading(false);
  };
  const handleTargetFilter = async (value: number) => {
    setTarget(value);
    setKey(listSalesRepresentative.target, value);
    setKey(listSalesRepresentative.page, 1);
    setLoading(true);
    setPage(1);
    await fetchSalesRepresentatives(
      1,
      pageLimit,
      search,
      status,
      value
    );
    setLoading(false);
  };
  const handleCurrentPage = async (val: number) => {
    setPage(val);
    setKey(listSalesRepresentative.page, val);
    await fetchSalesRepresentatives(val, pageLimit, search, status, target);
  };
  const handleNextPage = async (val: number) => {
    setPage(val + 1);
    setKey(listSalesRepresentative.page, val + 1);
    await fetchSalesRepresentatives(val + 1, pageLimit, search, status, target);
  };
  const handlePreviousPage = async (val: number) => {
    setPage(val - 1);
    setKey(listSalesRepresentative.page, val - 1);
    await fetchSalesRepresentatives(val - 1, pageLimit, search, status, target);
  };
  const handlePageLimit = async (event: any) => {
    setPage(1);
    setPageLimit(parseInt(event.target.value));
    setKey(listSalesRepresentative.page, 1);
    setKey(listSalesRepresentative.limit, parseInt(event.target.value));
    await fetchSalesRepresentatives(1, parseInt(event.target.value), search, status, target);
  };
  const isFiltered =
    search !== '' ||
    (status !== -1 && status !== undefined && status !== null) ||
    (target !== -1 && target !== undefined && target !== null);

  return (
    <>
      <div className="p-5">
        <Row className="align-items-center mb-5">
          <Col sm>
            <h1 className="fs-22 fw-bolder mb-0">{currentUser?.role.some((obj: any) => obj.type === Manager) ? "Sales " : "Activation "}Representative</h1>
          </Col>
          {(loading || (salesRepresentatives && salesRepresentatives.length > 0) || isFiltered) ? (
            <Col sm="auto">
              <div className="border border-r10px w-fit-content text-white">
                <button className="uw-btn uw-btn-primary uw-btn-lg btn btn-primary btn-lg fs-16 fw-600" onClick={() => setShowAddModal(true)}>Add new {currentUser?.role.some((obj: any) => obj.type === Manager) ? "Sales " : "Activation "}Representative</button>
              </div>
            </Col>
          ) : (
            <>
              <Col lg={12} className="mt-5">
                <div className="border border-r10px p-md-9 p-7">
                  <h2 className="fs-22 fw-bolder">
                    Start adding {currentUser?.role.some((obj: any) => obj.type === Manager) ? "Sales " : "Activation "}Representatives!
                  </h2>
                  <p className="fs-18 fw-500">
                      You can add {currentUser?.role.some((obj: any) => obj.type === Manager) ? "Sales " : "Activation "}Rep and manage them.
                  </p>
                  <div className="mt-5"></div>
                  <button className="uw-btn uw-btn-primary uw-btn-lg btn btn-primary btn-lg fs-16 fw-600" onClick={() => setShowAddModal(true)}>
                    Add new representative
                  </button>
                </div>
              </Col>
            </>
          )}
        </Row>
        <Row>
          <Col xs={12}>
            {loading || salesRepresentatives.length > 0 || isFiltered ? (
              <Card className="bg-light border my-7">
                <Card.Body className="px-7">
                  <Row className="align-items-center gy-5">
                    <Col sm={12} md={6} lg={4}>
                      <FormLabel className="fs-16 fw-500">
                        Search by Rep Name
                      </FormLabel>
                      <div className="d-flex align-items-center position-relative">
                        <KTSVG
                          path="/media/icons/duotune/general/gen021.svg"
                          className="svg-icon-3 position-absolute ms-3"
                        />
                        <input
                          type="text"
                          id="kt_filter_search"
                          className="form-control form-control-custom borde-r8px bg-white ps-11"
                          placeholder="Search"
                          value={search}
                          onChange={(event: any) => {
                            handleSearch(event.target.value);
                          }}
                        />
                      </div>
                    </Col>
                    <Col sm={12} md={6} lg={4}>
                      <FormLabel className="fs-16 fw-500">
                        Filter by status
                      </FormLabel>
                      <div>
                        <CustomSelect
                          backgroundColor="#ffff"
                          value={REPRESENTATIVE_STATUS.find(
                            (option) => option?.value == status
                          )}
                          options={REPRESENTATIVE_STATUS}
                          onChange={(event: any) =>
                            handleStatusFilter(event ? event.value : undefined)
                          }
                          isSearchable={false}
                          isMulti={false}
                          isClearable={true}
                          placeholder="Select"
                        />
                      </div>
                    </Col>
                    <Col sm={12} md={6} lg={4}>
                      <FormLabel className="fs-16 fw-500">
                        Target Period
                      </FormLabel>
                      <div>
                        <CustomSelect
                          backgroundColor="#ffff"
                          value={REPRESENTATIVE_TARGET_PERIOD.find(
                            (option) => option?.value == target
                          )}
                          options={REPRESENTATIVE_TARGET_PERIOD}
                          onChange={(event: any) =>
                            handleTargetFilter(event ? event.value : undefined)
                          }
                          isSearchable={false}
                          isMulti={false}
                          isClearable={true}
                          placeholder="Select"
                        />
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            ) : (
              <></>
            )}
          </Col>
          <Col lg={12}>
            {loading ? (
              <div className="d-flex justify-content-center m-4 h-200px">
                <Loader loading={loading} />
              </div>
            ) : (
              <>
                {salesRepresentatives.length > 0 || isFiltered ? (
                  <>
                    <Card className="border border-r10px">
                      <Card.Body className="p-0">
                        <div className="table-responsive">
                          <table className="table table-rounded table-row-bordered align-middle gs-7 gy-6 mb-0">
                            <thead>
                              <tr className="fw-bold fs-16 fw-600 text-dark border-bottom align-middle">
                                <th className="min-w-md-175px min-w-150px">
                                  Rep name
                                </th>
                                <th className="min-w-md-200px min-w-125px">
                                  Assigned Customers
                                </th>
                                <th className="min-w-md-125px text-left">
                                  Target
                                </th>
                                <th className="min-w-md-125px">
                                  Target Period
                                </th>
                                <th className="min-w-md-125px text-center">
                                  Status
                                </th>
                                <th className="min-w-150px text-end"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {salesRepresentatives.length > 0 ? (
                                salesRepresentatives.map((val: any, index: number) => {
                                  return (
                                    <tr key={val.id}>
                                      <td className="fs-15 fw-500">
                                        <div>{val.name}</div>
                                        <div className="text-muted">{val.email}</div>
                                      </td>
                                      <td className="fs-15 fw-500">
                                        {val?.currentSalesTarget?.totalAssignedCustomers ? <><div>{val?.currentSalesTarget?.totalAssignedCustomers} Customers</div> 
                                        <div className="text-muted fs-14">{val?.currentSalesTarget?.totalOrders ? val?.currentSalesTarget?.totalOrders : 0} Orders</div></>: "NA"}
                                      </td>
                                      <td className="fs-15 fw-500">
                                        <div>{val?.currentSalesTarget?.target ? `Tsh ${Method.getGeneralizedAmount(val?.currentSalesTarget?.achieved)} / Tsh ${Method.getGeneralizedAmount(val?.currentSalesTarget?.target)}` : "NA"}</div>
                                      </td>
                                      <td className="fs-15 fw-500">{Method.getSalesTargetTypesLabel(val?.currentSalesTarget?.type)}</td>
                                      <td className="fs-15 fw-500">
                                        <div className="d-flex justify-content-center">
                                          <label className="form-check form-switch form-check-custom form-check-solid ">
                                            <input
                                              className="form-check-input form-check-success w-50px h-30px "
                                              type="checkbox"
                                              name="status"
                                              onChange={() =>
                                                handleToggleActivation(
                                                  val,
                                                  index
                                                )
                                              }
                                              defaultChecked={
                                                val.status === 'Active'
                                              }
                                              checked={val.status === 'Active'}
                                            />
                                          </label>
                                        </div>
                                      </td>
                                      <td className="text-end">
                                      <div className="d-flex justify-content-end align-items-center gap-3 text-nowrap">
                                        <Button
                                          className="uw-btn uw-btn-primary fs-14 fw-600"
                                          onClick={() => {
                                            let currentScrollY = window.scrollY;
                                            setKey(listSalesRepresentative.scrollPosition, currentScrollY);
                                            navigateTo("/sales-management/manager/sales-representative/rep-details", {
                                              state: { id: val._id }
                                            });
                                          }}
                                        >
                                          View details
                                        </Button>
                                        <div className="my-0">
                                          <CustomSelectTable
                                            marginLeft={"-110px"}
                                            width={"auto"}
                                            placeholder={
                                              <img
                                                className="img-fluid"
                                                width={23}
                                                height={7}
                                                src={ThreeDotMenu}
                                                alt=""
                                              />
                                            }
                                            options={menuOptions}
                                            backgroundColor="white"
                                            show={showMenu && index === menuId}
                                            onMenuClose={onMenuClose}
                                            openMenuOnClick={() => openMenuOnClick(index)}
                                            onMenuOpen={() => onMenuOpen(index)}
                                            onChange={(event: any) => handleOption(event, val)}
                                          />
                                        </div>
                                      </div>
                                      </td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan={6} className="fs-15 fw-600 text-center">
                                    No Data Found
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </Card.Body>
                    </Card>
                    {!loading && salesRepresentatives.length > 0 ? (
                      <Pagination
                        totalRecords={totalRecords}
                        currentPage={page}
                        handleCurrentPage={handleCurrentPage}
                        handleNextPage={handleNextPage}
                        handlePreviousPage={handlePreviousPage}
                        handlePageLimit={handlePageLimit}
                        pageLimit={pageLimit}
                      />
                    ) : (
                      <></>
                    )}
                  </>
                ) : (
                  <></>
                )}
              </>
            )}
          </Col>
        </Row>
        <AddSalesRepresentativeModal
          show={showAddModal}
          onHide={() => {
            setShowAddModal(false);
            setSelectedRep(undefined);
          }}
          editData={selectedRep}
          onSave={() => {
            fetchSalesRepresentatives(page, pageLimit, search, status, target);
            setSelectedRep(undefined);
          }}
        />
      </div>
    </>
  );
};
export default SalesRepresentative;
